import Cropper from 'cropperjs'

const SELECTION_ID = 'image-cropper-selection'
const INITIAL_COVERAGE = 0.8

const CROPPER_TEMPLATE = `
  <cropper-canvas background>
    <cropper-image scalable translatable></cropper-image>
    <cropper-shade hidden></cropper-shade>
    <cropper-handle action="select" plain></cropper-handle>
    <cropper-selection id="${SELECTION_ID}" aspect-ratio="1" movable resizable>
      <cropper-grid role="grid" bordered covered></cropper-grid>
      <cropper-crosshair centered></cropper-crosshair>
      <cropper-handle action="move"></cropper-handle>
      <cropper-handle action="n-resize"></cropper-handle>
      <cropper-handle action="e-resize"></cropper-handle>
      <cropper-handle action="s-resize"></cropper-handle>
      <cropper-handle action="w-resize"></cropper-handle>
      <cropper-handle action="ne-resize"></cropper-handle>
      <cropper-handle action="nw-resize"></cropper-handle>
      <cropper-handle action="se-resize"></cropper-handle>
      <cropper-handle action="sw-resize"></cropper-handle>
    </cropper-selection>
  </cropper-canvas>
`

class ImageUploader {
  static selector = `form.edit_employee, form.new_employee, form.new_construction_object_employee, form.settings_edit_account,
                     form.new_tasker_user_group, form.edit_tasker_user_group, form.new_user, form.edit_user, form.new_company_user`

  constructor(form) {
    this.form = form
    this.cropper = null

    this.bindElements()

    if (this.selectors.fileInput) {
      const { avatarColors, src } = this.selectors.fileInput.dataset

      this.avatarColors = avatarColors && JSON.parse(avatarColors)
      form.querySelector('[name$="[colors]"]').value = this.avatarColors
      this.bindEvents()
      this.applyImage(src)
      this.toggleClearButton(src)
    }
  }

  bindElements() {
    this.selectors = {
      fileInput: this.form.querySelector('[data-image-upload-input'),
      destroyInput: this.form.querySelector('[name$="[purge]"'),
      clearButton: this.form.querySelector('[data-action=clear-image]'),
      resetButton: this.form.querySelector('[data-action=reset-image]'),
      zoomInButton: this.form.querySelector('[data-action=zoom-in-image]'),
      zoomOutButton: this.form.querySelector('[data-action=zoom-out-image]'),
      image: this.form.querySelector('#image-placeholder img'),
      imageCropper: this.form.querySelector('#image-cropper'),
      imagePreview: this.form.querySelector('#image-preview'),
      avatarAttributeInputs: this.form.querySelectorAll('[data-avatar-attribute]')
    }
  }

  bindEvents() {
    this.selectors.fileInput.addEventListener('change', this.onImageChange)
    this.selectors.clearButton.addEventListener('click', this.removeImage)
    this.selectors.resetButton.addEventListener('click', this.resetCropper)
    this.selectors.zoomInButton.addEventListener('click', this.zoomInCropper)
    this.selectors.zoomOutButton.addEventListener('click', this.zoomOutCropper)
    this.selectors.avatarAttributeInputs.forEach(input => input.addEventListener('keyup', this.applyInitials))
  }

  initialsSrc() {
    const { avatarApiUrl } = this.selectors.fileInput.dataset
    const initials = Array.from(this.selectors.avatarAttributeInputs, input => input.value.replace(' ', '+'))
      .filter(Boolean)
      .join('+')

    if (this.avatarColors && initials) {
      return `${avatarApiUrl}&background=${this.avatarColors[0]}&color=${this.avatarColors[1]}&name=${initials}`
    }
  }

  applyInitials = () => {
    if (!this.selectors.fileInput.dataset.src) {
      this.applyImage(this.initialsSrc())
    }
  }

  applyImage(src) {
    this.selectors.image.src = src || this.initialsSrc() || this.selectors.fileInput.dataset.placeholder
  }

  onImageChange = () => {
    this.destroyCropper()

    const file = this.selectors.fileInput.files[0]
    const src = file && URL.createObjectURL(file)

    if (file) {
      if (this.selectors.fileInput.dataset.src) {
        this.selectors.destroyInput.value = true
      } else {
        this.selectors.destroyInput.value = null
      }

      this.initCropper(src)
    }

    this.applyImage(src)
    this.toggleClearButton(src)
  }

  initCropper = src => {
    const image = this.selectors.imageCropper.querySelector('img')

    image.src = src
    this.toggleImagePreviewContainer()

    this.cropper = new Cropper(image, { template: CROPPER_TEMPLATE })

    const viewer = document.createElement('cropper-viewer')
    viewer.setAttribute('selection', `#${SELECTION_ID}`)
    viewer.setAttribute('resize', 'none')
    this.selectors.imagePreview.replaceChildren(viewer)

    const cropperImage = this.cropper.getCropperImage()
    const cropperSelection = this.cropper.getCropperSelection()

    cropperSelection.addEventListener('change', event => this.updateCropInputs(event.detail, this.imageRect()))
    cropperImage.addEventListener('change', event => this.updateCropInputs(this.selectionRect(), event.detail))
    cropperImage.$ready(() => this.selectInitialArea())
  }

  selectInitialArea() {
    const { x, y, width, height } = this.imageRect()
    const side = Math.min(width, height) * INITIAL_COVERAGE

    this.cropper.getCropperSelection().$change(x + (width - side) / 2, y + (height - side) / 2, side, side)
  }

  imageRect() {
    const canvasRect = this.cropper.getCropperCanvas().getBoundingClientRect()
    const { x, y, width, height } = this.cropper.getCropperImage().getBoundingClientRect()

    return { x: x - canvasRect.x, y: y - canvasRect.y, width, height }
  }

  selectionRect() {
    const { x, y, width, height } = this.cropper.getCropperSelection()

    return { x, y, width, height }
  }

  updateCropInputs(selection, imageRect) {
    if (!this.cropper || !imageRect.width) return

    const scale = this.cropper.getCropperImage().$image.naturalWidth / imageRect.width
    const form = this.form

    form.querySelector('[name$="[x]"]').value = Math.round((selection.x - imageRect.x) * scale)
    form.querySelector('[name$="[y]"]').value = Math.round((selection.y - imageRect.y) * scale)
    form.querySelector('[name$="[width]"]').value = Math.round(selection.width * scale)
    form.querySelector('[name$="[height]"]').value = Math.round(selection.height * scale)
  }

  removeImage = () => {
    this.selectors.fileInput.value = null
    this.selectors.destroyInput.value = true

    this.destroyCropper()
    this.onImageChange()
  }

  toggleClearButton(src) {
    if (src) {
      this.selectors.clearButton.classList.remove('hidden')
    } else {
      this.selectors.clearButton.classList.add('hidden')
    }
  }

  destroyCropper() {
    if (this.cropper) {
      this.toggleImagePreviewContainer()

      this.cropper.destroy()
      this.selectors.imagePreview.replaceChildren()
      this.cropper = null
    }
  }

  resetCropper = () => {
    if (!this.cropper) return

    this.cropper.getCropperImage().$resetTransform().$center('contain')
    this.selectInitialArea()
  }

  zoomInCropper = () => {
    this.cropper?.getCropperImage().$zoom(0.1)
  }

  zoomOutCropper = () => {
    this.cropper?.getCropperImage().$zoom(-0.1)
  }

  toggleImagePreviewContainer() {
    const { imagePreview, imageCropper } = this.selectors

    if (this.cropper) {
      imagePreview.classList.add('d-none')
      imageCropper.classList.add('d-none')
      imageCropper.querySelector('img').removeAttribute('src')
    } else {
      imagePreview.classList.remove('d-none')
      imageCropper.classList.remove('d-none')
    }
  }
}

export default ImageUploader
